import { Alert, Button, Card, CardContent, Field, Input } from '@blink/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ErrorAlert, PasswordInput } from '@/components/common';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';

/** Screen 0: username (mobile number or email) and password (D-0017). */
export function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  const queryClient = useQueryClient();
  const login = useMutation({
    mutationFn: () => api<{ mustChangePassword: boolean }>('/auth/login', { method: 'POST', body: { identifier, password } }),
    onSuccess: (res) => {
      // a shared phone: nothing kept from the driver who used it before (their parcels, cash)
      queryClient.clear();
      navigate(res.mustChangePassword ? '/change-password' : '/', { replace: true });
    },
    onError: () => setPassword(''),
  });

  const notice = params.get('reason') === 'expired' ? t.login.expired : params.get('reason') === 'signedout' ? t.login.signedOut : null;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-4">
      <div className="flex items-center gap-3">
        <img src="/icon.svg" alt="" className="h-12 w-12" />
        <div>
          <h1 className="text-2xl font-bold text-primary">{t.app.name}</h1>
          <p className="text-sm text-muted-foreground">{t.app.tagline}</p>
        </div>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              login.mutate();
            }}
          >
            <div>
              <h2 className="text-lg font-semibold">{t.login.title}</h2>
              <p className="text-sm text-muted-foreground">{t.login.subtitle}</p>
            </div>
            {notice && !login.error ? <Alert>{notice}</Alert> : null}
            <ErrorAlert error={login.error} />
            <Field label={t.login.identifier} htmlFor="identifier">
              <Input
                id="identifier"
                name="username"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                className="h-12 text-base"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </Field>
            <Field label={t.login.password} htmlFor="password">
              <PasswordInput id="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            <Button type="submit" size="lg" className="h-12 w-full text-base" disabled={login.isPending}>
              {t.login.submit}
            </Button>
            <p className="text-center text-xs text-muted-foreground">{t.login.forgot}</p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
